import { useCallback } from "react";
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import LiftingEquipmentForm from "src/components/pages/Settings/YachtSetUp/LiftingEquipmentForm/LiftingEquipmentForm";
import YachtSetupSectionHeader from "src/components/pages/Settings/YachtSetUp/YachtSetupSectionHeader";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { type ILink } from "src/interfaces";
import { PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import {
  type ILiftingEquipmentForm,
  type ILiftingEquipmentItem,
} from "./LiftingEquipment.types";

const emptyItem: ILiftingEquipmentItem = {
  name: "",
  description: "",
  certificates: [""],
  distinct: "",
};

const mapLiftingEquipment = (response: any): ILiftingEquipmentItem[] => {
  const list = response?.data?.content?.data?.list;
  if (!list || list.length === 0) return [emptyItem];
  return list.map((item: ILiftingEquipmentItem) => ({
    ...item,
    certificates:
      item.certificates && item.certificates.length > 0
        ? item.certificates
        : [""],
  }));
};

const LiftingEquipment = () => {
  const { data, isLoading: isGetLoading } = useGetApi({
    request: "get-lifting-equipment-list",
    body: { params: { yachtid: getYachtId() } },
    mapper: mapLiftingEquipment,
  });

  const getLinks = useCallback((links: ILink[]) => {
    return links.map((link, index) =>
      index === links.length - 1
        ? { ...link, title: "Lifting Equipment" }
        : link,
    );
  }, []);

  const { post, isLoading: isPostLoading } = usePostApi();

  const methods = useForm<ILiftingEquipmentForm>({
    values: {
      list: data ?? [emptyItem],
    },
  });

  const { append, ...fieldArrayProps } = useFieldArray<ILiftingEquipmentForm>({
    control: methods.control,
    name: "list",
  });

  const handleAppend = () => {
    append({ ...emptyItem, certificates: [""] });
  };

  const onSubmit: SubmitHandler<ILiftingEquipmentForm> = async formData => {
    await post({
      request: "save-lifting-equipment-list",
      body: {
        params: {
          yachtid: getYachtId(),
          data: formData.list,
        },
      },
      onSuccess: () => {
        Notification(
          "Success",
          "Lifting Equipment has been successfully saved.",
          "green",
        );
      },
    });
  };

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <YachtSetupSectionHeader
            isSaving={isPostLoading}
            section="lifting"
            onAdd={handleAppend}
            customLinks={getLinks}
          />
          {!data || isGetLoading ? (
            <Loader />
          ) : (
            <LiftingEquipmentForm {...fieldArrayProps} data={data} />
          )}
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export default LiftingEquipment;
